<template>
  <div class="drawer-tracked-routes extra-padding-x">

    <h1 class="drawer-header">
      <template v-if="!dbReady">Loading tracks...</template>
      <template v-else>
        Saved Tracks
        <span class="drawer-header-small">TOTAL: {{ totalItems }}</span>
      </template>
    </h1>

    <div class="edit-delete-buttons-wrapper-full-width">
      <div @click="toggleAllowEditingItemsButtons()" class="edit-txt">edit</div>
      <div @click="toggleAllowDeletingItemsButtons()" class="delete-txt">delete</div>
    </div>

    <!-- List of tracked routes: this solution differs from "Notes"! -->
    <div ref="list-of-tracked-routes" v-if="allTrackedRoutes" class="list-of-entries scrollbar">

      <div
        class="list-entry flex-row p-1 mb-3"
        v-for="(item, index) in allTrackedRoutes"
        :key="index"
        :data-id="item.id">

        <div class="flex-grow transition duration-500">

          <router-link
            :to="{ name: 'tracked-route', params: { id: item.id } }"
            :title="item.title"
            class="link"
            role="button">

            <div class="text-pink-400 flex flex-row w-full items-center justify-between">
              <div class="flex flex-row items-baseline">
                <span class="uppercase text-sm font-bold mr-1">{{ item.date | createdAt2date }}</span>
                <span class="text-xs">{{ item.date | createdAt2time }}</span>
              </div>
            </div>

            <div class="text-pink-100 font-bold uppercase mb-1">
              <template v-if="item.title">{{ item.title }}</template>
              <template v-else>[oldfile structure!] NAME {{ item.name }}</template>
            </div>

          </router-link>

        </div>

        <div class="edit-delete-icons-wrapper -mr-1">
          <!-- Delete item -->
          <svg @click="openModal(item.id)" class="delete-icon">
            <use xlink:href="#cross" />
          </svg>
          <!-- Edit item -->
          <svg @click="editOneItem(item)" class="edit-icon">
            <use xlink:href="#notes" />
          </svg>
        </div>

      </div>

    </div>

    <Pagination
      class="my-3"
      :itemsPerPage=itemsPerPage
      :totalItems=totalItems
      @onSkimmingThePages="loadTrackedRoutes"
    />

    <Editor
      :setup=setup
      :mode=mode
      :toggleClass=toggleClass
      :id=id
      :title=title
      :content=content
      :createdAt=createdAt
      :feedback=feedback
      @onAddItem='addItem'
      @onUpdateItem='updateItem'
      @onResetEditor='resetMode'
    />

  </div>
</template>

<script>
import GeoMoverDB from '@/db/indexeddb'
import Editor from '@/components/Editor'
import Pagination from '@/components/Pagination'
import { EventBus } from '@/eventBus'

export default {
  name: 'TrackedRoutes',
  components: { Editor, Pagination },
  data () {
    return {
      //
      // ----- Props for Editor
      //
      setup: 'full', // 'full', 'min'
      // "mode" is used by editor to call upon its state machine
      mode: 'new', // edit, preEdit, delete, idle
      // this class is put on the body; react to it
      toggleClass: 'tracked-routes-editor-active',
      // The item to edit / add (when infos are sent back from editor)
      id: null,
      title: null,
      content: null,
      createdAt: null,
      // The message after "save", "cancel" etc. action was (un-)successful
      feedback: null,
      //
      // ----- Props for Pagination
      //
      totalItems: 0,
      currentPage: 0,
      //
      // ----- Local attributes
      //
      // When editing, save the whole item locally, too, instead of only
      // sending to the editor. This is, because we only edit the "name" of the
      // track in the editor, nothing else. But when we save again, we need the
      // rest of the item's infos.
      editItem: {},
      // Toggle between "edit items" and "delete items". Their status defines,
      // which "mode" is sent to the Editor child component.
      allowDeletingItems: false,
      allowEditingItems: false,
      // 'id' of rackedRoute to be deleted. Used in GeoMoverDB
      idOfItemToDelete: null
    }
  },
  computed: {
    dbReady: {
      // Initiate dependent function calls in "watch" (see below)
      // => way cleaner and more stable solution, since the watch guarantees
      //    that the depending function will only be called once
      //    calling funcs within get() causes only troubles, mutiple calls etc
      // Could be a problem with the Logger mixin, though
      get () { return this.$store.getters['settings/getDbReady'] }
    },
    // Currently this is also valid for the paginated tracked routes option
    allTrackedRoutes: {
      get () { return this.$store.getters['trackedRoutes/getAllTrackedRoutes'] }
    },
    itemsPerPage: {
      get () { return parseInt(this.$store.getters['settings/getMaxTrackedRoutesPerPage']) }
    },
    iconsVisible: {
      get () {
        if (this.mode === 'edit' || this.mode === 'preEdit' || this.mode === 'delete') {
          return 'w-6 ml-2'
        }
        return 'w-0 ml-0'
      }
    },
    /**
     * @description See activeMenu watcher below for description.
     * @requires activeMenu watcher
     */
    activeMenu: {
      get () { return this.$store.getters['ui/getActiveMenu'] }
    }
  },
  methods: {
    /**
     * ----- PAGINATION
     */
    // Called from Pagination child component
    loadTrackedRoutes (page) {
      // // this.$l.debug('TrackedRoutes', 'loadTrackedRoutes invoked\npage', page)
      this.currentPage = page
      this.getPaginatedTrackedRoutes()
    },
    /**
     * ----- EDITOR
     */
    // Called from Editor child component 'onResetEditor'
    resetMode () {
      if (this.mode !== 'delete') {
        this.mode = 'new'
      }
      /// It would be possible to deactivate the EDIT mode, if "edit" was
      /// opened right now, an item to edit was assigned and then the editor
      /// component's header was clicked in order to initiate the "toggle",
      /// which would now only close the editor and remove the item's texts
      /// from the input fields.
      /// We could close EDIT mode, too, but maybe the editor was just closed
      /// in order to look for another item to be edited. In this case keep
      /// EDIT mode active.
      /// If we do not want to keep it active, uncomment the following 3 lies.
      // if (this.mode !== 'edit') {
      // document.body.classList.remove('tracked-routes-edit')
      // }
      this.allowEditingItems = false
    },
    toggleAllowEditingItemsButtons () {
      document.body.classList.toggle('tracked-routes-edit')
      document.body.classList.remove('tracked-routes-delete')

      this.allowEditingItems = !this.allowEditingItems
      this.allowDeletingItems = false

      this.mode = !this.allowEditingItems ? 'new' : 'preEdit'
    },
    toggleAllowDeletingItemsButtons () {
      document.body.classList.toggle('tracked-routes-delete')
      document.body.classList.remove('tracked-routes-edit')

      this.allowEditingItems = false
      this.allowDeletingItems = !this.allowDeletingItems

      /// Prop for editor child component
      this.mode = !this.allowDeletingItems ? 'new' : 'delete'
    },
    /**
     * ----- "CRUD" METHODS TODO: Add visual feedbacks for the user
     */
    // Add
    async addItem (obj) {
      // this.$l.debug('TrackedRoutes', 'addItem obj', obj)

      const getLastId = GeoMoverDB.getLastId('trackedRoutes')
      const lastId = await getLastId

      // Clean obj.content from new lines and "jsonify" it
      const jsonContent = JSON.parse(obj.content.replace(/(\n|\t)/g, ''))
      jsonContent.id = lastId + 1

      const newItemToSave = {
        title: obj.title,
        date: Date.now(),
        geojson: jsonContent,
        id: lastId + 1,
        //
        // Add missing params (that cannot be added via the editor form)
        //
        // The following have not been defined; they should either be
        // retrieved from geojson.properties (if they exist there) or
        // calculates anew (like distance, duration). Not all items can
        // be added, if the source is not a GeoMover item that contains
        // all those informations on its geojson.properties param.
        distanceKm: jsonContent.features[0].properties.distanceKm || 0,
        distanceM: jsonContent.features[0].properties.distanceM || 0,
        durationInSeconds: jsonContent.features[0].properties.durationInSeconds || 0,
        durationStartStop: jsonContent.features[0].properties.durationStartStop || 0,
        steps: jsonContent.features[0].properties.steps || 0,
        timerDisplay: jsonContent.features[0].properties.timerDisplay || 0,
        totalDistance: jsonContent.features[0].properties.totalDistance || 0,
        typeTitle: jsonContent.features[0].properties.typeTitle || 0
      }

      // Save to DB
      const savedTrack = GeoMoverDB.addOne('trackedRoutes', newItemToSave)
      savedTrack
        .then(success => {
          // this.$l.success('TrackedRoutes', 'Manually added track saved successfully\nid of added track route', success)

          this.feedback = 'New track saved.'
          // Reload all items; the "obj.id" is sent to the pagination module in order
          // to evoke a visual feedback on the updated item
          this.getPaginatedTrackedRoutes(lastId + 1)
        })
        .catch(error => {
          this.$l.error('TrackedRoutes', 'save: error', error.id + ': ' + error.message)
        })
        .finally(() => {
          this.clearFeedbackTimeout()
          this.resetProps()
          this.updateTotalCountOfTrackedRoutes()
        })
    },
    // Edit & update
    editOneItem (item) {
      // Update Props (sent to Editor component)
      this.mode = 'edit' // see Editor -> watch:mode
      this.id = item.id
      this.title = item.title
      this.content = item.content || ''
      this.toggleClass = 'tracked-routes-editor-active'
      // Save the complete "note to be edited", because we need all the
      // "other parts" (like km, distance etc) again, when we save the item
      // again, which is nothing else but overwriting the object in the DB
      this.editItem = item
    },
    async updateItem (obj) {
      // This causes trouble when id = 0, so compare against... what?? Definitely not against 0!!!
      if (obj.id === null) {
        this.feedback = 'No note selected.'
        return false
      }

      // Rebuild the item with the edited content (here only the title = name)
      // Get the rest of the original info from the locally saved copy at
      // "this.editItem" (assigned in "func editOneItem")
      const updatedContent = {
        title: obj.title,
        content: obj.content,
        updated: Date.now()
        // id: obj.id,
        // date: this.editItem.date,
        // geojson: this.editItem.geojson,
      }

      const editedItemObj = {}

      // MErge edited Item with original item which has more properties that
      // were not edited
      Object.assign(editedItemObj, this.editItem, updatedContent)

      const result = GeoMoverDB.updateOne('trackedRoutes', editedItemObj, this.editItem.id)
      result
        .then(success => {
          this.feedback = 'Update successful.'
          // Reload all items; the "obj.id" is sent to the pagination module in order
          // to evoke a visual feedback on the updated item
          this.getPaginatedTrackedRoutes(this.editItem.id)
        })
        .catch(error => {
          this.$l.log('TrackedRoutes', 'updateItem\nerror =', error)
          this.feedback = 'Update error.'
        })
        .finally(() => {
          this.clearFeedbackTimeout()
          this.resetProps()
        })
    },
    // Delete
    async deleteItem () {
      // this.$l.info('TrackedRoutes', 'deleteItem invoked', '')

      const deleteResult = GeoMoverDB.deleteOne('trackedRoutes', this.idOfItemToDelete)
      deleteResult
        .then(success => {
          this.feedback = 'Item deleted.'
          this.getPaginatedTrackedRoutes()
          this.updateTotalCountOfTrackedRoutes()
        })
        .catch((error) => {
          this.$l.error('TrackedRoutes', 'deleteItem - try-catch error: There was an error in the delete process of a tracked route. What now?\nerror =', error)
          this.feedback = 'Delete error.'
        })
    },
    /**
     * ----- FETCH CONTENT
     */
    async getPaginatedTrackedRoutes (idOfChangedItem = null) {
      // this.$l.info('TrackedRoutes', 'getPaginatedTrackedRoutes invoked', '')
      // this.$l.debug('TrackedRoutes', 'this.itemsPerPage', this.itemsPerPage)

      // GeoMoverDB.findSelected('trackedRoutes', 'createdAt', 'desc', [limit], [offset])
      const items = await GeoMoverDB.findSelection('trackedRoutes', 'date', 'desc', this.itemsPerPage, this.currentPage)

      // If this is not async, the "indicateChange" function will throw a promise error
      await this.$store.dispatch('trackedRoutes/setAllTrackedRoutes', items)

      // If there was an item that was added or update, display a visual indicator
      // this.$l.debug('TrackedRoutes', 'getPaginatedTrackedRoutes\nidOfChangedItem', idOfChangedItem)

      if (idOfChangedItem != null) {
        // this.$l.success('TrackedRoutes', 'getPaginatedTrackedRoutes\nidOfChangedItem', idOfChangedItem)
        this.indicateChange(idOfChangedItem)
      }
    },
    // Display total number of trackedRoutes next to header
    async calculateTotalCountOfTrackedRoutes () {
      this.totalItems = await GeoMoverDB.getTotalCount('trackedRoutes')
    },
    /**
     * Called once an item was deleted or added, in order to update
     * the display of the pager, the number next to the header, etc
     */
    updateTotalCountOfTrackedRoutes () {
      // Just call the totalcount at the moment...
      this.calculateTotalCountOfTrackedRoutes()
    },
    /**
     * Provide a visual indication for a just recently changed item
     * @ param { numb er|st ring } id - The id of the item that was changed or added
     */
    indicateChange (id) {
      // this.$l.debug('TrackedRoutes', 'indicateChange invoked with id', id)

      if (id != null) {
        const changedItem = document.querySelector('[data-id="' + id + '"]')

        // Add a background color
        changedItem.classList.add('item-changed')

        // Remove the bg again after a while
        setTimeout(() => {
          changedItem.classList.remove('item-changed')
        }, 2000)
      }
    },
    /**
     * "Cleanup" methods
     */
    resetProps () {
      // these should actually reach down to the editor... but it seems they do not
      this.id = null
      this.title = null
      this.content = null
      this.createdAt = null
      // clear the "backup" copy of the edited item
      this.editItem = null
    },
    clearFeedbackTimeout () {
      // "this.mode" is sent to the editor component to change state to "idle"
      setTimeout(() => {
        this.mode = 'idle'
        this.feedback = null
      }, 1000)
    },
    /**
     * ----- MODAL INTERACTION
     */
    openModal (id) {
      // // this.$l.info('TrackedRoutes', 'openModal\nItem to delete id = ',  id)
      this.idOfItemToDelete = id

      // we must pass object params with all the information
      const params = {
        mode: 'confirm',
        title: 'Attention',
        text: 'Are you sure you want to delete this route?This action is not undoable!',
        // we are passing callback method for our confirm button
        onConfirm: () => {
          return this.deleteItem()
        }
      }

      this.$modal.show(params)
    },
    cancelDeleteAction () {
      // // this.$l.info('TrackedRoutes', 'cancelDeleteAction via click invoked, '')
      this.idOfItemToDelete = null
    }
  },
  mounted () {
    // this.$l.info('TrackedRoutes', 'mounted', '')
    EventBus.$on('setEditorToIdleState', () => { this.setEditorToIdleState() })
    this.getPaginatedTrackedRoutes()
  },
  watch: {
    /**
     * We need to observe activeMenu in order to reset the editor of a menu
     * component to its idle state. This is especially necessary, since under
     * certain circumstances the text "Deactivate Delete Menu" will not be
     * reset.
     *
     * @example
     * 1. open tracked routes
     * 2. click on "delete" so that is becomes active
     *    The text in the editor not reads "Deactive delete mode"
     * 3. Change to another menu, e.g. Notes
     *    This should actually reset the delete buttons of the previous menu
     *    in case they were active, and should also reset the Editor...
     * 4. Change back to tracked menu
     *    Normally the text in the editor would still read "Deactivate delete mode"
     *    The "activeMenu" watcher, however, resets the Editor state to "idle"
     *    when the oldVal matches this component.
     *
     * @requires activeMenu in computed properties
     *
     */
    activeMenu: function (newVal, oldVal) {
      if (oldVal === 'tracked-routes') {
        this.mode = 'new'
      }
    },
    itemsPerPage: async function (newVal, oldVal) {
      // only when db is ready, the global settings are set, and with them
      // the pagination settings, which will invoke this watcher; so
      // call db when 'itemsPerPage' ready, NOT on dbReady!
      // Sets this.totalItems (no return value)
      await this.calculateTotalCountOfTrackedRoutes()

      if (this.totalItems !== 0) {
        this.getPaginatedTrackedRoutes()
      }
      else {
        // this.$l.log('TrackedRoutes', 'itemsPerPage invoked\nThere have no routes been recorded yet.', '')
      }
    }
  }
}
</script>

<!--
    <div class="[ editor ] px-2 flex flex-col flex-shrink-0 content-between w-full mt-auto">
      <div class="flex flex-row items-center justify-between mb-3 select-none pt-2 border-t border-pink-700">
        <div class="text-xs tracking-wider uppercase text-pink-600 ml-auto [ new ]">Edit options</div>
        <div class="font-bold [ edit ]">Edit note</div>
        <div class="w-full [ delete ]">Deactivate Delete Mode</div>
        <svg class="w-4 h-4 [ editor-toggle-icon ]">
          <use v-if="editorActive" xlink:href="#cross" />
          <use v-else xlink:href="#notes" />
        </svg>
      </div>
      <div class="[ editor-wrapper ] flex flex-col flex-grow items-start justify-start overflow-hidden transition-all duration-500 ease-in-out p-1">
        <input
          placeholder="Edit name of tracked route"
          v-model="editItemName"
          class="w-full mb-2 rounded-md p-2 text-gray-700"
        />
        <div class="flex flex-row items-center justify-between w-full [ edit ]">
          <span v-if="feedback" class="text-pink-200 mr-1 leading-tight select-none">{{ feedback }}</span>
          <button @click="updateItem()" class="btn hover:bg-pink-600 ml-auto select-none">Update</button>
        </div>
      </div>
    </div>
-->
